// File: src/pages/reveal/RevealIntroWizardPage.jsx
import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LuArrowLeft, LuPartyPopper } from 'react-icons/lu';
import {
  IoDocumentTextOutline,
  IoShieldCheckmarkOutline,
  IoChatbubblesOutline,
  IoTimeOutline,
  IoPeopleOutline,
} from 'react-icons/io5';
import EventHelpTooltip from '../../components/EventPreview/EventHelpTooltip';
import { ReactComponent as TribeLogo } from '../../assets/TribeLogo.svg';

/* ───────────────────────── Visual Tokens (Update/Register-aligned) */
const COLOR = {
  bg: '#FFFFFF',
  ink: '#0B1020',
  sub: '#3A4050',
  line: '#E7E9EE',
  chip: '#F3F4F6',
  chipBorder: '#E5E7EB',
  tagBg: '#F7F8FA',
  tagInk: '#333',
  tagBorder: '#E7E9EE',
  iconBg: '#F3F6FF',
  iconInk: '#2553E6',
};
const GRIDW = 'min(720px, 92vw)';

/**
 * RevealIntroWizardPage
 * - Navigates back to the Reveal event detail page
 * - Proceeds to the Reveal quiz route
 * Paths used:
 *   Back:  /events/reveal-:eventId
 *   Quiz:  /reveal/events/:eventId/quiz
 */
export default function RevealIntroWizardPage() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const api = process.env.REACT_APP_API_URL;

  const event = state?.event || {};
  const eventId = String(event?._id || '');
  const title = useMemo(() => (event?.name ? event.name : 'This Reveal'), [event?.name]);
  const purpose = useMemo(() => event?.purposeTag || 'General', [event?.purposeTag]);
  const truncatedPurpose = useMemo(() => {
    if (!purpose) return '';
    return purpose.length > 22 ? purpose.slice(0, 21) + '…' : purpose;
  }, [purpose]);

  const handleQuiz = async () => {
    if (!eventId) {
      navigate('/events');
      return;
    }
    try {
      const res = await axios.get(`${api}/reveal-mode/${eventId}/access`, {
        validateStatus: s => s === 200 || s === 401 || s === 403,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken') || ''}`,
          'X-Suppress-Global-Error': '1',
        },
      });

      // Allowed → go to quiz (even if alreadySubmitted; portal path handled elsewhere)
      if (res.status === 200 && res.data?.ok) {
        navigate(`/reveal/${eventId}/quiz`, { state: { event } });
        return;
      }

      // Denied → branch by reason and bounce to Event page with correct sheet
      const reason = res?.data?.reason || '';
      if (reason === 'quiz_required' || reason === 'quiz_incomplete') {
        navigate(`/reveal/${eventId}/quiz`, { state: { event } });
        return;
      }
      if (reason === 'not_registered') {
        navigate(`/events/reveal-${eventId}`, { state: { event, showRegSheet: true } });
        return;
      }
      if (reason === 'out_of_fence' || reason === 'no_location') {
        navigate(`/events/reveal-${eventId}`, { state: { event, showGeoSheet: true } });
        return;
      }
      if (reason === 'gender_gate') {
        navigate(`/events/reveal-${eventId}`, { state: { event, showOrientationDenied: true } });
        return;
      }
      if (reason === 'too_early') {
        navigate(`/events/reveal-${eventId}`, { state: { event } });
        return;
      }

      // Fallback
      navigate(`/events/reveal-${eventId}`, { state: { event } });
    } catch {
      navigate(`/events/reveal-${eventId}`, { state: { event } });
    }
  };

  const handleBack = () => {
    if (eventId) {
      navigate(`/events/reveal-${eventId}`, { state: { event } });
    } else {
      navigate('/events');
    }
  };

  return (
    <div style={S.app}>
      {/* Header: black back pill, centered logo, tooltip */}
      <header style={S.header}>
        <button style={S.backBtn} onClick={handleBack} aria-label="Go back">
          <LuArrowLeft size={16} />
        </button>

        <div style={S.logoWrap} aria-hidden="true">
          <TribeLogo style={S.logoSvg} />
        </div>

        <div style={S.rightWrap}>
          <div style={S.headerRightStack}>
            <EventHelpTooltip
              title="About the Reveal Quiz"
              body="A quick check-in so we can set you up with the right one-on-one reveal. Answer honestly—it only takes a minute."
            />
          </div>
        </div>
      </header>

      {/* Page body */}
      <main style={S.page}>
        <div style={S.headerWrap}>
          <h1 style={S.headerTop}>TAKE THE QUIZ TO ENTER</h1>
          <div style={S.headerBottom}>{title}</div>
          <p style={S.subheading}>
            This quick quiz helps us line up your reveal—pairing you by personality and preferences.
          </p>
        </div>

        {/* Tags */}
        <div style={S.tagRow}>
          <div style={S.tag} title="Quiz Required">
            <IoDocumentTextOutline size={14} style={{ marginRight: 6 }} />
            Quiz Required
          </div>
          {!!truncatedPurpose && (
            <div style={S.tagLight} title={purpose}>
              <LuPartyPopper size={14} style={{ marginRight: 6 }} />
              {truncatedPurpose}
            </div>
          )}
        </div>

        {/* Values & Expectations */}
        <section style={S.card} aria-labelledby="values-title">
          <div style={S.cardHeader}>
            <span id="values-title" style={S.cardTitle}>
              Values &amp; Expectations
            </span>
          </div>
          <div style={S.cardBody}>
            <ul style={S.list}>
              <li style={S.listItem}>
                <div style={S.icon}><IoShieldCheckmarkOutline size={16} /></div>
                <div>
                  <div style={S.itemHead}>Respect first</div>
                  <div style={S.itemBody}>Kindness, consent, and inclusion. Zero tolerance for harassment.</div>
                </div>
              </li>

              <li style={S.listItem}>
                <div style={S.icon}><IoChatbubblesOutline size={16} /></div>
                <div>
                  <div style={S.itemHead}>Be present</div>
                  <div style={S.itemBody}>Phones away during reveals. Listen actively and share the space.</div>
                </div>
              </li>

              <li style={S.listItem}>
                <div style={S.icon}><IoTimeOutline size={16} /></div>
                <div>
                  <div style={S.itemHead}>Honor the timer</div>
                  <div style={S.itemBody}>Arrive on time, follow prompts, and move when the portal cues you.</div>
                </div>
              </li>

              <li style={S.listItem}>
                <div style={S.icon}><IoPeopleOutline size={16} /></div>
                <div>
                  <div style={S.itemHead}>Participation</div>
                  <div style={S.itemBody}>Engage in each round, respond clearly, and opt-in or out respectfully after chats.</div>
                </div>
              </li>
            </ul>
          </div>
        </section>
      </main>

      {/* Fixed bottom CTA */}
      <div style={S.buttonBar} role="group" aria-label="Actions">
        <div style={S.buttonRow}>
          <button style={S.primaryBtn} onClick={handleQuiz}>Take Quiz</button>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── Styles (Inter, hairlines, black CTAs, 10px radii) */
const S = {
  app: {
    fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    color: COLOR.ink,
    background: COLOR.bg,
  },

  page: {
    width: GRIDW,
    margin: '14px auto 96px',
    display: 'grid',
    gap: 16,
  },

  /* Header */
  header: {
    position: 'sticky',
    top: 0,
    zIndex: 100,
    width: '100%',
    maxWidth: 1200,
    margin: '0 auto',
    height: 56,
    display: 'grid',
    alignItems: 'center',
    gridTemplateColumns: '1fr auto 1fr',
    background: '#fff',
  },
  backBtn: {
    justifySelf: 'start',
    marginLeft: 12,
    background: '#000',
    color: '#fff',
    border: 'none',
    borderRadius: 9999,
    width: 40,
    height: 26,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  logoWrap: {
    justifySelf: 'center',
    display: 'flex',
    alignItems: 'center',
    pointerEvents: 'none',
  },
  logoSvg: {
    width: 72,
    height: 'auto',
    display: 'block',
  },
  rightWrap: {
    justifySelf: 'end',
    marginRight: 12,
    display: 'flex',
    alignItems: 'center',
    height: 32,
  },
  headerRightStack: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },

  /* Headline block */
  headerWrap: {
    textAlign: 'center',
    maxWidth: 640,
    margin: '0 auto',
  },
  headerTop: {
    fontSize: 'clamp(0.98rem, 2.6vw, 1.05rem)',
    fontWeight: 900,
    letterSpacing: '0.06em',
    lineHeight: 1.15,
    margin: '10px 0 0 0',
    color: '#000',
  },
  headerBottom: {
    fontSize: 'clamp(1.8rem, 4.2vw, 2.2rem)',
    lineHeight: 1.25,
    marginTop: 8,
    fontWeight: 900,
    letterSpacing: '-0.01em',
  },
  subheading: {
    fontSize: 13.5,
    color: COLOR.sub,
    lineHeight: 1.55,
    margin: '10px 28px 0 28px',
  },

  /* Tags */
  tagRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: 8,
    marginTop: 10,
    marginBottom: 6,
    flexWrap: 'wrap',
  },
  tag: {
    display: 'inline-flex',
    alignItems: 'center',
    background: COLOR.tagBg,
    color: '#0E7A4E',
    borderRadius: 999,
    padding: '6px 12px',
    fontSize: 12.5,
    fontWeight: 800,
    border: `1px solid ${COLOR.tagBorder}`,
  },
  tagLight: {
    display: 'inline-flex',
    alignItems: 'center',
    background: COLOR.tagBg,
    color: COLOR.tagInk,
    borderRadius: 999,
    padding: '6px 12px',
    fontSize: 12.5,
    fontWeight: 700,
    maxWidth: 220,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    border: `1px solid ${COLOR.tagBorder}`,
  },

  /* Values Card */
  card: {
    width: '100%',
    border: `1px solid ${COLOR.line}`,
    borderRadius: 12,
    background: '#FFFFFF',
    overflow: 'hidden',
    boxShadow: '0 1px 2px rgba(16,24,40,0.05)',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
  },
  cardTitle: {
    fontSize: 14.5,
    fontWeight: 900,
    color: COLOR.ink,
    letterSpacing: '0.01em',
  },
  cardBody: {
    borderTop: `1px solid ${COLOR.line}`,
    padding: '10px 16px 14px',
  },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'grid',
    gap: 10,
  },
  listItem: {
    display: 'grid',
    gridTemplateColumns: '22px 1fr',
    gap: 10,
    alignItems: 'start',
  },
  icon: {
    width: 22,
    height: 22,
    display: 'grid',
    placeItems: 'center',
    background: COLOR.iconBg,
    color: COLOR.iconInk,
    borderRadius: 999,
    marginTop: 2,
  },
  itemHead: {
    fontWeight: 800,
    color: COLOR.ink,
    marginBottom: 2,
    fontSize: 14,
    letterSpacing: 0.1,
  },
  itemBody: {
    color: '#4A5568',
    lineHeight: 1.5,
    fontSize: 13.25,
  },

  /* CTA bar */
  buttonBar: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    padding: '12px 20px calc(12px + env(safe-area-inset-bottom))',
    background: 'transparent',
    pointerEvents: 'none',
  },
  buttonRow: {
    pointerEvents: 'auto',
    margin: '0 auto',
    width: '100%',
    maxWidth: 'none',
    display: 'flex',
  },
  primaryBtn: {
    flex: 1,
    height: 44,
    borderRadius: 10,
    border: '1px solid #0b0b0b',
    background: '#0b0b0b',
    color: '#fff',
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
};
